import { z } from 'zod';
import { CurrencyCode, MinorUnits } from './money';
import { HouseEdgeBps, ZeroEdgeStatusView } from './money';
import { LobbyPayoutRule } from './lobby';

// ---------------------------------------------------------------------------
// Fairness
// ---------------------------------------------------------------------------

export const CrashFairnessScheme = z.literal('crash-drand-hmac-v1');
export type CrashFairnessScheme = z.infer<typeof CrashFairnessScheme>;

// ---------------------------------------------------------------------------
// Game config
// ---------------------------------------------------------------------------

export const CrashGameConfigView = z.object({
  kind: z.literal('CRASH'),
  gameCode: z.string(),
  name: z.string(),
  mathVersion: z.string(),
  houseEdgeBps: HouseEdgeBps,
  maxMultiplier: z.number(),
  /** Milliseconds the betting window stays open. */
  bettingWindowMs: z.number().int().positive(),
  /** Milliseconds between rounds. */
  intermissionMs: z.number().int().nonnegative(),
  /** Curve parameter: multiplier doubles every `doublingMs` milliseconds. */
  doublingMs: z.number().int().positive(),
});
export type CrashGameConfigView = z.infer<typeof CrashGameConfigView>;

// ---------------------------------------------------------------------------
// Lobby state machine
// ---------------------------------------------------------------------------

export const CrashRoundState = z.enum([
  'SCHEDULED',
  'BETTING_OPEN',
  'LOCKED',
  'RUNNING',
  'CRASHED',
  'SETTLING',
  'CLOSED',
  'VOIDED',
]);
export type CrashRoundState = z.infer<typeof CrashRoundState>;

export const CrashEntryState = z.enum([
  'BET_PENDING',
  'ACTIVE',
  'CASHED_OUT',
  'LOST',
  'SETTLE_PENDING',
  'CLOSED',
  'ROLLBACK_PENDING',
  'ROLLED_BACK',
]);
export type CrashEntryState = z.infer<typeof CrashEntryState>;

// ---------------------------------------------------------------------------
// Bet / cashout requests & responses
// ---------------------------------------------------------------------------

export const CrashBetRequest = z.object({
  amount: MinorUnits,
  /** If set, the server cashes out automatically at this multiplier. */
  autoCashout: z.number().min(1.01).optional(),
  clientEntryId: z.uuid(),
});
export type CrashBetRequest = z.infer<typeof CrashBetRequest>;

export const CrashBetResponse = z.object({
  status: z.literal('OK'),
  entryId: z.uuid(),
  roundId: z.uuid(),
  balance: MinorUnits,
  zeroEdge: ZeroEdgeStatusView.nullable().optional(),
});
export type CrashBetResponse = z.infer<typeof CrashBetResponse>;

export const CrashCashoutResponse = z.object({
  status: z.literal('OK'),
  entryId: z.uuid(),
  multiplier: z.number(),
  payout: MinorUnits,
  balance: MinorUnits,
  settled: z.boolean(),
  /** The entry's effective edge at cashout, after any exposure scaling. */
  houseEdgeBps: z.number().int().optional(),
});
export type CrashCashoutResponse = z.infer<typeof CrashCashoutResponse>;

// ---------------------------------------------------------------------------
// Lobby state snapshot (for state endpoint + reconnect)
// ---------------------------------------------------------------------------

export const CrashEntryView = z.object({
  entryId: z.uuid(),
  playerId: z.string(),
  displayName: z.string().nullable(),
  amount: MinorUnits,
  currency: CurrencyCode,
  state: CrashEntryState,
  autoCashout: z.number().nullable(),
  cashoutMultiplier: z.number().nullable(),
  payout: MinorUnits.nullable(),
});
export type CrashEntryView = z.infer<typeof CrashEntryView>;

export const CrashLobbyStateResponse = z.object({
  status: z.literal('OK'),
  lobbyId: z.string(),
  roundId: z.uuid().nullable(),
  state: CrashRoundState,
  serverSeedHash: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .nullable(),
  drandRound: z.number().int().nullable(),
  /** ISO-8601. Null until RUNNING. */
  startedAt: z.string().nullable(),
  /** Null until CRASHED. */
  crashPoint: z.number().nullable(),
  /** Revealed after crash. */
  serverSeed: z.string().nullable(),
  drandRandomness: z.string().nullable(),
  /** ISO-8601 betting close time. Null when not in BETTING_OPEN. */
  closesAt: z.string().nullable(),
  entries: z.array(CrashEntryView),
  lastSequence: z.number().int(),
  /** Recent crash points for history display. */
  recentCrashPoints: z.array(z.number()),
  config: CrashGameConfigView,
  zeroEdge: ZeroEdgeStatusView.nullable().optional(),
});
export type CrashLobbyStateResponse = z.infer<typeof CrashLobbyStateResponse>;

// ---------------------------------------------------------------------------
// Round history (paginated)
// ---------------------------------------------------------------------------

export const CrashRoundHistoryEntry = z.object({
  roundId: z.uuid(),
  roundNumber: z.number().int().nullable(),
  crashPoint: z.number(),
  closedAt: z.string(),
});
export type CrashRoundHistoryEntry = z.infer<typeof CrashRoundHistoryEntry>;

export const CrashRoundHistoryResponse = z.object({
  status: z.literal('OK'),
  rounds: z.array(CrashRoundHistoryEntry),
  nextBefore: z.number().int().nullable(),
});
export type CrashRoundHistoryResponse = z.infer<typeof CrashRoundHistoryResponse>;

// ---------------------------------------------------------------------------
// Per-round fairness proof (retrievable by round id)
// ---------------------------------------------------------------------------

export const CrashProofResponse = z.object({
  status: z.literal('OK'),
  roundId: z.uuid(),
  roundNumber: z.number().int().nullable().optional(),
  state: z.string().optional(),
  scheme: CrashFairnessScheme,
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  serverSeed: z.string().nullable(),
  drandRound: z.number().int().nullable(),
  drandRandomness: z.string().nullable(),
  drandSignature: z.string().nullable(),
  crashPoint: z.number().nullable(),
});
export type CrashProofResponse = z.infer<typeof CrashProofResponse>;

// ---------------------------------------------------------------------------
// Round result (for operator reports and player history)
// ---------------------------------------------------------------------------

export const CrashRoundResult = z.object({
  kind: z.literal('CRASH'),
  /** What the entry paid, in minor units. Every stored result carries this. */
  totalWin: MinorUnits,
  gameCode: z.string(),
  mathVersion: z.string(),
  crashPoint: z.number(),
  cashoutMultiplier: z.number().nullable(),
  serverSeedHash: z.string(),
  serverSeed: z.string().nullable(),
  drandRound: z.number().int().nullable(),
  drandRandomness: z.string().nullable(),
  scheme: CrashFairnessScheme,
});
export type CrashRoundResult = z.infer<typeof CrashRoundResult>;

// ---------------------------------------------------------------------------
// SSE events (discriminated by `type`, all carry monotonic `sequence`)
// ---------------------------------------------------------------------------

const CrashEventBase = z.object({
  sequence: z.number().int(),
  roundId: z.uuid(),
  timestamp: z.string(),
});

export const CrashEventRoundCreated = CrashEventBase.extend({
  type: z.literal('round_created'),
  serverSeedHash: z.string().regex(/^[0-9a-f]{64}$/),
  drandRound: z.number().int(),
});

export const CrashEventBettingOpen = CrashEventBase.extend({
  type: z.literal('betting_open'),
  closesAt: z.string(),
});

export const CrashEventBettingLocked = CrashEventBase.extend({
  type: z.literal('betting_locked'),
});

export const CrashEventRoundStarted = CrashEventBase.extend({
  type: z.literal('round_started'),
  startedAt: z.string(),
  /**
   * The curve this round follows, exactly as its engine returned it. The client
   * animates from this rather than from a built-in constant, so a game that
   * changes its curve does not need a client release.
   */
  payoutRule: LobbyPayoutRule.optional(),
});

export const CrashEventBetPlaced = CrashEventBase.extend({
  type: z.literal('bet_placed'),
  entryId: z.uuid(),
  playerId: z.string(),
  displayName: z.string().nullable(),
  amount: MinorUnits,
  currency: CurrencyCode,
});

export const CrashEventCashedOut = CrashEventBase.extend({
  type: z.literal('cashed_out'),
  entryId: z.uuid(),
  playerId: z.string(),
  multiplier: z.number(),
  payout: MinorUnits,
});

export const CrashEventRoundCrashed = CrashEventBase.extend({
  type: z.literal('round_crashed'),
  crashPoint: z.number(),
  serverSeed: z.string(),
  drandRandomness: z.string(),
  drandRound: z.number().int(),
  scheme: CrashFairnessScheme,
});

export const CrashEventRoundVoided = CrashEventBase.extend({
  type: z.literal('round_voided'),
  reason: z.string(),
});

export const CrashEvent = z.discriminatedUnion('type', [
  CrashEventRoundCreated,
  CrashEventBettingOpen,
  CrashEventBettingLocked,
  CrashEventRoundStarted,
  CrashEventBetPlaced,
  CrashEventCashedOut,
  CrashEventRoundCrashed,
  CrashEventRoundVoided,
]);
export type CrashEvent = z.infer<typeof CrashEvent>;

export const CrashEventsResponse = z.object({
  status: z.literal('OK'),
  events: z.array(CrashEvent),
});
export type CrashEventsResponse = z.infer<typeof CrashEventsResponse>;

// ---------------------------------------------------------------------------
// WebSocket messages (not persisted — ephemeral broadcast only)
// ---------------------------------------------------------------------------

export const CrashWsMultiplierTick = z.object({
  type: z.literal('multiplier_tick'),
  roundId: z.uuid(),
  serverTime: z.string(),
  multiplier: z.number(),
});
export type CrashWsMultiplierTick = z.infer<typeof CrashWsMultiplierTick>;

export const CrashWsStateSnapshot = z
  .object({
    type: z.literal('state'),
  })
  .merge(CrashLobbyStateResponse.omit({ status: true }));
export type CrashWsStateSnapshot = z.infer<typeof CrashWsStateSnapshot>;

export const CrashWsHeartbeat = z.object({
  type: z.literal('heartbeat'),
});

/**
 * Discriminated union of all messages the server can send over the crash WS.
 * Round events share the same shapes as SSE events; ticks and state snapshots
 * are WS-only.
 */
export type CrashWsServerMessage =
  CrashWsStateSnapshot | CrashWsMultiplierTick | CrashEvent | { type: 'heartbeat' };
